import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Video,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Clock,
  Award,
} from 'lucide-react'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const services = [
    'Event Management',
    'Video Production',
    'Corporate Events',
    'Photography',
    'Live Streaming',
    'Wedding Coverage',
  ]

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Contact', path: '/contact' },
    { name: 'Book Event', path: '/booking' },
  ]

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Youtube, href: '#', label: 'YouTube' },
    { icon: Twitter, href: '#', label: 'Twitter' },
  ]

  return (
    <footer className="relative mt-20">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      
      <div className="relative section-padding bg-black/30">
        <div className="container-custom">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold gradient-text">
                  Parth Video
                </span>
              </div>
              
              <p className="text-white/70 leading-relaxed">
                Professional video production and event management company dedicated to capturing your most precious moments with creativity and excellence.
              </p>
              
              <div className="flex items-center space-x-2 text-white/60">
                <Award className="w-5 h-5 text-primary-400" />
                <span className="text-sm">10+ Years of Excellence</span>
              </div>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <h3 className="text-xl font-semibold text-white">Our Services</h3>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link
                      to="/services"
                      className="text-white/70 hover:text-primary-300 transition-colors duration-300 text-sm flex items-center space-x-2 group"
                    >
                      <span className="w-1.5 h-1.5 bg-primary-400 rounded-full group-hover:scale-125 transition-transform duration-300" />
                      <span>{service}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <h3 className="text-xl font-semibold text-white">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="text-white/70 hover:text-primary-300 transition-colors duration-300 text-sm flex items-center space-x-2 group"
                    >
                      <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full group-hover:scale-125 transition-transform duration-300" />
                      <span>{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              <h3 className="text-xl font-semibold text-white">Get in Touch</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/70 text-sm">Call Us</p>
                    <a href="tel:+917874467874" className="text-white hover:text-primary-300 transition-colors duration-300">
                      +91 7874467874
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-secondary-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/70 text-sm">Email Us</p>
                    <a href="mailto:bhavesh7874@gmail.com" className="text-white hover:text-secondary-300 transition-colors duration-300">
bhavesh7874@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-accent-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/70 text-sm">Visit Us</p>
                    <p className="text-white text-sm leading-relaxed">
                      Ahmedabad, India
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/70 text-sm">Business Hours</p>
                    <p className="text-white text-sm">
                      Mon - Sat: 9AM - 8PM<br />
                      Sun: 10AM - 6PM
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Social Media & Bottom Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="pt-8 border-t border-white/20"
          >
            <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
              {/* Social Links */}
              <div className="flex items-center space-x-4">
                <span className="text-white/70 text-sm mr-2">Follow Us:</span>
                {socialLinks.map((social, index) => {
                  const Icon = social.icon
                  return (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-full glass hover:bg-white/20 transition-all duration-300 group"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                    >
                      <Icon className="w-5 h-5 text-white/70 group-hover:text-white transition-colors duration-300" />
                    </motion.a>
                  )
                })}
              </div>

              {/* Copyright */}
              <div className="text-center md:text-right">
                <p className="text-white/60 text-sm">
                  © {currentYear} Parth Video. All rights reserved.
                </p>
                <p className="text-white/40 text-xs mt-1">
                  Crafted with ❤️ by{' '}
                  <a 
                    href="https://trifusion.chat" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity duration-300"
                  >
                    <span className="bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent font-extrabold text-sm">
                      Trifusion
                    </span>
                  </a>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}

export default Footer